import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X, MapPin } from "lucide-react";
import PageHero from "@/components/PageHero";
import Newsletter from "@/components/Newsletter";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { GALLERY_VISITS, GALLERY_FOREST } from "@/data/content";

function Lightbox({ photo, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-ink/95 flex items-center justify-center p-4 sm:p-12"
      onClick={onClose}
      data-testid="gallery-lightbox"
    >
      <button
        onClick={onClose}
        aria-label="Close photo"
        className="absolute top-5 right-5 w-11 h-11 rounded-full border border-paper/30 flex items-center justify-center text-paper hover:bg-terra hover:border-terra transition-colors duration-300"
        data-testid="gallery-lightbox-close"
      >
        <X className="w-5 h-5" />
      </button>
      <motion.figure
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-4xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <img src={photo.src} alt={photo.alt} className="w-full max-h-[75vh] object-contain rounded-lg" data-testid="gallery-lightbox-image" />
        {photo.caption && <figcaption className="text-center text-paper/70 text-sm mt-5 font-mono">{photo.caption}</figcaption>}
      </motion.figure>
    </motion.div>
  );
}

function VisitTile({ photo, onOpen, className = "" }) {
  return (
    <Reveal className={className}>
      <button
        onClick={() => onOpen(photo)}
        className="group relative block w-full text-left rounded-lg overflow-hidden border border-line focus:outline-none focus:ring-2 focus:ring-terra/60"
        data-testid={`gallery-tile-${photo.tag.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      >
        <img src={photo.src} alt={photo.alt} className="w-full h-72 lg:h-96 object-cover photo-tone group-hover:scale-[1.04] transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-ink/10 to-transparent opacity-90" aria-hidden="true" />
        <div className="absolute bottom-0 inset-x-0 p-5">
          <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-gold bg-ink/60 backdrop-blur-sm rounded-full px-3 py-1">{photo.tag}</span>
          <p className="text-paper font-serif text-lg mt-3 leading-snug">{photo.caption}</p>
        </div>
      </button>
    </Reveal>
  );
}

export default function Gallery() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    document.title = "Gallery — Jen Cullerton Johnson";
  }, []);

  return (
    <>
      <PageHero
        eyebrow="Gallery"
        title={["Jen in the", <span key="g"><em className="text-terra">wild</em> — visits, classrooms & woods</span>]}
        lede="A look at the work in action: school visits and book events, professional development with educators, and quiet moments on the trail."
        meta="Chicago · Indiana Dunes · Schools & libraries everywhere"
      />

      <section className="py-20 lg:py-28" data-testid="gallery-visits">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow className="mb-6">Author visits & events</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mb-14">Bringing books to life</h2>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
            {GALLERY_VISITS.map((p) => (
              <VisitTile key={p.src} photo={p} onOpen={setActive} className={p.span} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream border-y border-line py-20 lg:py-28" data-testid="gallery-forest">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow className="mb-6">Forest bathing</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mb-6">Mornings among the trees</h2>
            <p className="text-txtmut max-w-2xl leading-relaxed mb-14 flex items-start gap-3">
              <MapPin className="w-5 h-5 text-terra shrink-0 mt-1" />
              <span>
                From still mornings in Humboldt Park to the whispering trails of Thatcher Woods and the dunes of
                Lake Michigan — the landscapes where Jen guides slow, sensory walks, and where she first learned
                to listen.
              </span>
            </p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 lg:gap-6">
            {GALLERY_FOREST.map((p, i) => (
              <Reveal key={p.src} delay={i * 0.06} className={i === 0 ? "col-span-2 lg:col-span-2 row-span-2" : "col-span-1 lg:col-span-2"}>
                <div className="group rounded-lg overflow-hidden border border-line h-full">
                  <img
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    className={`w-full object-cover photo-tone group-hover:scale-[1.05] transition-transform duration-700 ${i === 0 ? "h-72 lg:h-[460px]" : "h-40 lg:h-52"}`}
                  />
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <p className="font-serif text-2xl text-ink italic">"Nature is not elsewhere; it's here, waiting between the hours of our busy lives."</p>
              <Link to="/collaborate" className="group inline-flex items-center gap-2 bg-terra hover:bg-terradark text-paper rounded-full px-7 py-3.5 font-medium transition-colors duration-300 shrink-0" data-testid="gallery-forest-cta">
                Book a forest session <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Newsletter />

      <AnimatePresence>{active && <Lightbox photo={active} onClose={() => setActive(null)} />}</AnimatePresence>
    </>
  );
}
